import {
  ButtonGroup,
  GhostButton,
  PrimaryButton,
  Stack,
  Text,
  Space,
} from "@certn/cascadia";
import { Divider, SectionMessage } from "@certn/cascadia-react";
import { CascadiaControlProps, CascadiaJsonFormContext } from "@certn/forms";
import { useContext, useMemo } from "react";

import { Trans, useTranslation } from "react-i18next";
import { css } from "@emotion/css";
import { useApplication } from "@certn/application";

import { Formik, FormikErrors, FormikProps, FormikTouched } from "formik";
import {
  buildCustomQuestionnaireFormSchema,
  isConditionFulfilled,
} from "../custom-questionnaire/utils";
import isEqual from "lodash/isEqual";
import merge from "lodash/merge";
import * as Yup from "yup";
import {
  FitnessProprietyAnswerType,
  FitnessProprietyFormType,
  FITNESS_PROPRIETY_KEY,
} from "./types";
import { FitnessProprietyLayoutContext } from "../../layouts/FitnessProprietyLayoutContext";
import { FitnessField } from "./FitnessField";

export function FitnessProprietyControl(props: CascadiaControlProps) {
  const { path } = props;
  const { t } = useTranslation();

  const {
    sectionLabel,
    subLabel,
    templateData,
    apiError: error,
  } = useContext(FitnessProprietyLayoutContext);

  const {
    submit,
    setValues: setContextValues,
    values: applicantFormData,
  } = useContext(CascadiaJsonFormContext);

  const { prevSection, navigateToSection } = useApplication();

  const templateIds = JSON.stringify(templateData?.templates.map((t) => t.id));

  const initialValues: FitnessProprietyFormType[] = useMemo(
    () =>
      (templateData?.templates ?? []).map((template, index) => {
        const isCurrent = isEqual(index, 0);
        const answers: FitnessProprietyAnswerType =
          Object.fromEntries(
            template.template_input_claims.map((claim) => {
              const answer =
                applicantFormData[FITNESS_PROPRIETY_KEY]?.[claim.name] ??
                undefined;

              return [claim.name, answer];
            })
          ) ?? {};

        return {
          templateId: template.id,
          name: template.name,
          isCurrent,
          claims: [...template.template_input_claims],
          conditions: [...template.template_input_claim_conditions],
          answers,
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [templateIds]
  );

  if (error) {
    return (
      <SectionMessage
        type="danger"
        sectionTitle={t(
          "An error occurred while loading fitness & propriety templates. Please try again later."
        )}
        icon="error"
      >
        {error.message}
      </SectionMessage>
    );
  }

  if (!templateData || templateData.templates.length === 0) {
    return (
      <Stack>
        <SectionMessage
          icon="warning"
          sectionTitle={t("No fitness & propriety templates available.")}
          type="danger"
        />
      </Stack>
    );
  }

  const updateContextValues = (input: {
    claim: string;
    answer: string | number | boolean | null;
    hasCompletedRequiredFitnessProprietyQuestions: boolean;
  }) => {
    const { claim, answer, hasCompletedRequiredFitnessProprietyQuestions } =
      input;

    const answers = {
      ...applicantFormData[FITNESS_PROPRIETY_KEY],
      [claim]: answer === "" ? null : answer,
    };

    const templates = templateData?.templates || [];
    const conditions = templates
      .filter((template) =>
        template.template_input_claim_conditions.some(
          (c) => c.parent_claim_key === claim
        )
      )
      .flatMap((template) => template.template_input_claim_conditions);

    const conditionalClaims = conditions.filter(
      (conditionalClaim) =>
        !isConditionFulfilled({
          condition: conditionalClaim,
          answers,
        })
    );

    const conditionalAnswers = conditionalClaims.reduce(
      (acc: FitnessProprietyAnswerType, conditionalClaim) => {
        acc[conditionalClaim.dependent_claim_key] = null;
        return acc;
      },
      {}
    );

    setContextValues({
      ...applicantFormData,
      [FITNESS_PROPRIETY_KEY]: {
        ...answers,
        ...conditionalAnswers,
      },
      [path]: hasCompletedRequiredFitnessProprietyQuestions,
    });
  };

  const handleNext = async (
    values: FitnessProprietyFormType[],
    setValues: FormikProps<FitnessProprietyFormType[]>["setValues"],
    validateForm: () => Promise<FormikErrors<FitnessProprietyFormType[]>>,
    setTouched: (
      touched: FormikTouched<FitnessProprietyFormType>[],
      shouldValidate?: boolean
    ) => void
  ) => {
    const currentTemplate = values.find((value) => !!value.isCurrent);
    const templates = templateData?.templates || [];

    const currentIndex = templates.findIndex(
      (t) => t.id === currentTemplate?.templateId
    );

    const errors = await validateForm();
    const currentTemplateErrors = errors?.[currentIndex];
    const hasCurrentTemplateErrors =
      currentTemplateErrors &&
      typeof currentTemplateErrors === "object" &&
      "answers" in currentTemplateErrors &&
      currentTemplateErrors.answers &&
      Object.keys(currentTemplateErrors.answers).length > 0;

    if (hasCurrentTemplateErrors) {
      const touched: FormikTouched<FitnessProprietyFormType>[] = values.map(
        (_, index) => {
          if (index !== currentIndex) return {};
          const answersTouched: Record<string, boolean> = {};
          currentTemplate?.claims.forEach((claim) => {
            answersTouched[claim.name] = true;
          });
          return { answers: answersTouched };
        }
      );
      setTouched(touched, true);
      return;
    }

    const isLastTemplate = currentIndex === templates.length - 1;

    const answers = merge({}, ...values.map((value) => value.answers));

    // Derive completion from the fresh validateForm() result above, not from
    // Formik's cached `isValid`. `isValid` lags behind the latest field change
    // by one render, so reading it at click time can submit a stale `false`
    // even when every answer is filled in (causes a 400 from the backend on
    // the first click after answering the last question).
    //
    // Formik returns `errors` as an object keyed by index (e.g. {"0": {...}})
    // rather than a real array, so iterate via Object.values to be safe.
    const allTemplatesValid = Object.values(errors ?? {}).every(
      (templateErrors) =>
        !templateErrors ||
        typeof templateErrors !== "object" ||
        !("answers" in templateErrors) ||
        !templateErrors.answers ||
        Object.keys(templateErrors.answers).length === 0
    );
    const hasEmptyAnswers = values.some((value) =>
      Object.values(value.answers).some((answer) => answer === "")
    );
    const hasCompletedRequiredFitnessProprietyQuestions =
      allTemplatesValid && !hasEmptyAnswers;

    setContextValues({
      ...applicantFormData,
      [FITNESS_PROPRIETY_KEY]: answers,
      [path]: hasCompletedRequiredFitnessProprietyQuestions,
    });

    if (isLastTemplate) {
      submit();
    } else {
      const nextIndex = currentIndex + 1;

      setValues((prev) =>
        prev.map((p, index) => ({
          ...p,
          isCurrent: index === nextIndex,
        }))
      );
    }
  };

  const handlePrevious = async (
    values: FitnessProprietyFormType[],
    setValues: FormikProps<FitnessProprietyFormType[]>["setValues"]
  ) => {
    const currentTemplate = values.find((value) => !!value.isCurrent);
    const templates = templateData?.templates || [];

    const currentIndex = templates.findIndex(
      (t) => t.id === currentTemplate?.templateId
    );
    const isFirstTemplate = currentIndex === 0;

    if (isFirstTemplate && prevSection) {
      navigateToSection(prevSection);
    } else {
      const previousIndex = currentIndex - 1;

      setValues((prev) =>
        prev.map((p, index) => ({
          ...p,
          isCurrent: index === previousIndex,
        }))
      );
    }
  };

  const isConditionalQuestion = (input: {
    name: string;
    values: FitnessProprietyFormType[];
  }): { isConditional: boolean; isConditionFulfilled: boolean } => {
    const { name, values } = input;

    const currentTemplate = values.find((value) => !!value.isCurrent);
    const currentTemplateData = templateData.templates.find(
      (c) => c.id === currentTemplate?.templateId
    );

    if (!currentTemplateData) {
      return { isConditional: false, isConditionFulfilled: true };
    }

    const conditions = currentTemplateData.template_input_claim_conditions;

    const condition = conditions.find(
      (condition) => condition.dependent_claim_key === name
    );

    if (!condition) {
      return { isConditional: false, isConditionFulfilled: true };
    }

    const answers = currentTemplate?.answers ?? {};

    return {
      isConditional: true,
      isConditionFulfilled: isConditionFulfilled({
        condition,
        answers,
      }),
    };
  };

  return (
    <Stack gap="space-6">
      <Formik<FitnessProprietyFormType[]>
        initialValues={initialValues}
        enableReinitialize
        validateOnMount
        validationSchema={Yup.array().of(
          Yup.lazy((value: FitnessProprietyFormType) =>
            buildCustomQuestionnaireFormSchema(
              value.claims || [],
              value.conditions || [],
              value.answers ?? {},
              t
            )
          )
        )}
        onSubmit={(_, { setSubmitting }) => {
          setSubmitting(false);
        }}
      >
        {({ values, setValues, isValid, validateForm, setTouched }) => {
          const hasEmptyValues = values.some((v) =>
            Object.values(v.answers).some((value) => value === "")
          );
          const hasCompletedRequiredFitnessProprietyQuestions =
            isValid && !hasEmptyValues;

          const currentTemplate = values.find((value) => !!value.isCurrent);

          return (
            <Stack gap="space-8">
              <div className={styles.heading}>
                <Text as="h1" style="form/page-title" testID="template-title">
                  {currentTemplate?.name ||
                    sectionLabel ||
                    t("Fitness & Propriety Declarations")}
                </Text>

                <Text
                  style="form/body-primary"
                  color="text-weak"
                  testID="consent-sub-title"
                >
                  {subLabel}
                </Text>
              </div>

              <Stack gap="space-4">
                {currentTemplate?.claims.map((claim, index) => {
                  const { isConditional, isConditionFulfilled } =
                    isConditionalQuestion({
                      name: claim.name,
                      values,
                    });

                  if (isConditional && !isConditionFulfilled) return null;

                  const currentTemplateIndex = values.indexOf(currentTemplate);
                  const showDivider = !isEqual(index, 0) && !isConditional;

                  return (
                    <Stack gap="space-0" key={claim.name}>
                      {showDivider && <Divider marginVertical="space-4" />}
                      <FitnessField
                        {...claim}
                        name={`[${currentTemplateIndex}].answers.${claim.name}`}
                        onChange={(value) => {
                          updateContextValues({
                            claim: claim.name,
                            answer: value,
                            hasCompletedRequiredFitnessProprietyQuestions,
                          });
                        }}
                      />
                    </Stack>
                  );
                })}

                <div className={styles.actionBar}>
                  <ButtonGroup>
                    {prevSection && (
                      <GhostButton
                        leftIcon="chevron_left"
                        onPress={() => {
                          handlePrevious(values, setValues);
                        }}
                        testID="section-back-button"
                      >
                        <Trans>Back</Trans>
                      </GhostButton>
                    )}
                    <Space />
                    <PrimaryButton
                      onPress={() => {
                        handleNext(values, setValues, validateForm, setTouched);
                      }}
                      testID="section-next-step-button"
                    >
                      <Trans>Next step</Trans>
                    </PrimaryButton>
                  </ButtonGroup>
                </div>
              </Stack>
            </Stack>
          );
        }}
      </Formik>
    </Stack>
  );
}

const styles = {
  heading: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-2)",
  }),
  actionBar: css({
    paddingTop: `calc(${"var(--space-4)"} - 1px)`,
    paddingBottom: "var(--space-4)",
    marginTop: "var(--space-5)",
    borderTop: "1px solid var(--border-base)",
  }),
};
